import { useCallback, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  addComment,
  deleteComment,
  getComments,
  updateComment,
} from '../../../shared/api/comments.api'
import { useAuthStore } from '../../auth/authStore'
import type { Comment } from '../../../shared/api/types'
import { MentionInput, type MentionInputHandle } from './MentionInput'
import { CommentItem } from './CommentItem'
import { ReplyHintBar } from './ReplyHintBar'
import { AttachImageButton, DraftImages, canSendComment, useCommentImages } from './commentImages'

export function CommentsPanel({
  requestId,
  filterForProcurement = false,
  requestManagerId,
}: {
  requestId: string
  /** Если true — скрываем комментарии других закупщиков */
  filterForProcurement?: boolean
  requestManagerId?: string
}) {
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [mentions, setMentions] = useState<string[]>([])
  const [replyTo, setReplyTo] = useState<Comment | null>(null)
  const { user } = useAuthStore()
  const inputRef = useRef<MentionInputHandle>(null)
  const images = useCommentImages()

  // Клик «Ответить» → сразу фокус на textarea, чтобы можно было печатать
  // без второго клика. RAF подождёт, пока ReplyHintBar смонтируется и
  // textarea получит финальный layout.
  const handleReply = useCallback((c: Comment) => {
    setReplyTo(c)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  const { data: comments = [] } = useQuery({
    queryKey: ['comments', 'request', requestId],
    queryFn: () => getComments({ requestId }),
  })

  const addMutation = useMutation({
    mutationFn: () =>
      addComment({
        text,
        requestId,
        mentions,
        parentId: replyTo?.id ?? null,
        imageIds: images.ids,
      }),
    onSuccess: () => {
      setText('')
      images.clear()
      setMentions([])
      setReplyTo(null)
      void queryClient.invalidateQueries({ queryKey: ['comments', 'request', requestId] })
    },
  })

  const canSend = canSendComment(text, images) && !addMutation.isPending
  const send = () => {
    if (canSend) addMutation.mutate()
  }

  const updateMutation = useMutation({
    mutationFn: ({ id, nextText }: { id: string; nextText: string }) =>
      updateComment(id, { text: nextText }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', 'request', requestId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: (_data, id) => {
      // Если удалили коммент, на который сейчас отвечаем — сбрасываем плашку.
      if (replyTo?.id === id) setReplyTo(null)
      void queryClient.invalidateQueries({ queryKey: ['comments', 'request', requestId] })
    },
  })

  const handleSubmitEdit = useCallback(
    async (id: string, nextText: string) => {
      await updateMutation.mutateAsync({ id, nextText })
    },
    [updateMutation],
  )

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteMutation.mutateAsync(id)
    },
    [deleteMutation],
  )

  const handleParentClick = useCallback((parentId: string) => {
    // Скроллим к родительскому комменту в этом же списке.
    const el = document.querySelector<HTMLElement>(`[data-focus-id="${parentId}"]`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('ring-2', 'ring-brand-300')
      setTimeout(() => el.classList.remove('ring-2', 'ring-brand-300'), 1500)
    }
  }, [])

  // Фильтрация для закупщика: скрываем комментарии других закупщиков
  const visibleComments: Comment[] = filterForProcurement
    ? comments.filter((c) => {
        if (c.userId === user?.id) return true // свои
        if (c.userId === requestManagerId) return true // менеджер продаж заявки
        if (c.user?.role === 'ADMIN') return true // руководство
        if (c.user?.role === 'PROCUREMENT') return false // другие закупщики — скрыть
        return true // менеджеры продаж без явного id
      })
    : comments

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto space-y-2 max-h-72 pr-1">
        {visibleComments.length === 0 ? (
          <p className="text-xs text-gray-400 py-2">Комментариев нет</p>
        ) : (
          visibleComments.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              currentUserId={user?.id}
              onReply={user ? handleReply : undefined}
              onSubmitEdit={user ? handleSubmitEdit : undefined}
              onDelete={user ? handleDelete : undefined}
              onParentClick={handleParentClick}
            />
          ))
        )}
      </div>
      {user && (
        <div className="mt-3 space-y-1">
          {replyTo && <ReplyHintBar parent={replyTo} onClear={() => setReplyTo(null)} />}
          <DraftImages state={images} />
          <div className="flex items-end gap-2">
            <MentionInput
              ref={inputRef}
              value={text}
              onChange={(t, m) => {
                setText(t)
                setMentions(m)
              }}
              onSubmit={send}
              onPasteFiles={images.addFiles}
              allowEmptySubmit={canSend}
              requestId={requestId}
              disabled={addMutation.isPending}
            />
            <AttachImageButton state={images} disabled={addMutation.isPending} />
            <button
              onClick={send}
              disabled={!canSend}
              className="px-3 py-1.5 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors"
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
